import { useInfiniteList, useOne } from "@refinedev/core";
import { Job, Batch, Brand } from "../../../../pages/playground/types";
import { JOBS_PAGE_SIZE } from "../constants";
import { useInfiniteScrollObserver } from "./use-infinite-scroll-observer";

export interface UseJobListDataReturn {
  jobs: Job[];
  batch: Batch | undefined;
  brand: Brand | undefined;
  isLoadingBatch: boolean;
  isLoadingBrand: boolean;
  isLoadingJobs: boolean;
  isError: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  refetch: () => Promise<unknown>;
  refetchBatch: () => Promise<unknown>;
  observerTarget: React.RefObject<HTMLDivElement>;
}

/**
 * Hook to fetch and manage job list data
 *
 * Fetches:
 * - Jobs (with infinite scroll pagination)
 * - Batch data
 * - Brand data (dependent on batch)
 *
 * @param batchId - The batch ID to fetch jobs for (can be undefined)
 * @returns Job list data and refetch functions
 */
export function useJobListData(
  batchId: string | undefined
): UseJobListDataReturn {
  const {
    data,
    isError,
    isLoading: isLoadingJobs,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteList({
    resource: "job",
    pagination: {
      pageSize: JOBS_PAGE_SIZE,
    },
    meta: {
      params: {
        batch_id: batchId,
      },
    },
    queryOptions: {
      enabled: !!batchId,
    },
  });

  const {
    data: batchData,
    isLoading: isLoadingBatch,
    refetch: refetchBatch,
  } = useOne({
    resource: "batch",
    id: batchId,
    queryOptions: {
      enabled: !!batchId,
    },
  });

  const { data: brandData, isLoading: isLoadingBrand } = useOne({
    resource: "brand",
    id: batchData?.data?.brand_id,
    queryOptions: {
      enabled: !!batchData?.data?.brand_id,
    },
  });

  const jobs = (data?.pages?.map((page) => page.data).flat() || []) as Job[];
  const batch = batchData?.data as Batch;
  const brand = brandData?.data as Brand;

  const { observerTarget } = useInfiniteScrollObserver(
    hasNextPage || false,
    isFetchingNextPage,
    isLoadingJobs,
    fetchNextPage
  );

  return {
    jobs,
    batch,
    brand,
    isLoadingBatch,
    isLoadingBrand,
    isLoadingJobs,
    isError,
    hasNextPage: hasNextPage || false,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    refetchBatch,
    observerTarget,
  };
}
